import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import EventBusyRoundedIcon from '@mui/icons-material/EventBusyRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { DateCalendar, PickersDay, type PickersDayProps } from '@mui/x-date-pickers';
import { addDays, format, isSameDay, startOfDay } from 'date-fns';
import { fr as frLocale } from 'date-fns/locale';
import { useApiError } from '../../shared/errors/useApiError';
import { useNotification } from '../../shared/notifications/NotificationProvider';
import {
  bookingsService,
  CANCELLATION_NOTICE_DAYS,
  CANCELLATION_REFUND_PERCENT,
  type Booking,
} from '../../shared/services/bookings.service';
import { BookingCard } from './BookingCard';
import { BookingFilterTabs } from './BookingFilterTabs';
import { useBookingList } from './useBookingList';
import { BookingChatDrawer } from '../chat/BookingChatDrawer';
import { LeaveReviewDialog } from './LeaveReviewDialog';

type ViewMode = 'list' | 'calendar';

function bookingDayKey(b: Booking): string | null {
  if (!b.preferredDateTime) return null;
  return format(new Date(b.preferredDateTime), 'yyyy-MM-dd');
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Mirrors {@code BookingsService.computeRefundCents} on the server so the
 * customer sees the exact figure before confirming the cancellation.
 */
function computeRefundPreview(b: Booking): {
  refundCents: number;
  totalCents: number;
  currency: string | null;
  refundable: boolean;
  daysUntil: number | null;
} {
  const total = b.services.reduce((sum, s) => sum + (s.priceCents ?? 0), 0);
  const currency = b.services.find((s) => s.currency)?.currency ?? null;
  const daysUntil = b.preferredDateTime
    ? Math.floor((new Date(b.preferredDateTime).getTime() - Date.now()) / MS_PER_DAY)
    : null;
  const refundable = daysUntil != null && daysUntil >= CANCELLATION_NOTICE_DAYS && total > 0;
  const refundCents = refundable ? Math.floor((total * CANCELLATION_REFUND_PERCENT) / 100) : 0;
  return { refundCents, totalCents: total, currency, refundable, daysUntil };
}

function formatPrice(cents: number, currency: string | null): string {
  if (!currency) return `${(cents / 100).toFixed(2)}`;
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

export function CustomerBookingsPage() {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language?.startsWith('fr') ? frLocale : undefined;
  const { notify } = useNotification();
  const { translate } = useApiError();
  const { filter, setFilter, bookings, isLoading, invalidate } = useBookingList('customer');
  const [pendingId, setPendingId] = useState<string | null>(null);

  const [view, setView] = useState<ViewMode>('list');
  const [selectedDate, setSelectedDate] = useState<Date>(() => startOfDay(new Date()));

  const [chatBookingId, setChatBookingId] = useState<string | null>(null);
  const [requestTarget, setRequestTarget] = useState<Booking | null>(null);
  const [requestMessage, setRequestMessage] = useState('');
  const [reviewTarget, setReviewTarget] = useState<Booking | null>(null);

  // Calendar view — confirmed appointments only (status the provider has
  // accepted, including those with a pending cancellation request that the
  // provider hasn't decided on yet).
  const calendarQuery = useQuery({
    queryKey: ['bookings', 'customer', 'calendar'],
    queryFn: async () => (await bookingsService.listMine({ page: 0, size: 200 })).data,
    enabled: view === 'calendar',
  });

  const calendarBookings = useMemo<Booking[]>(() => {
    const data = calendarQuery.data ?? [];
    return data.filter(
      (b) =>
        (b.status === 'CONFIRMED' || b.status === 'CANCELLATION_REQUESTED') &&
        b.preferredDateTime != null,
    );
  }, [calendarQuery.data]);

  const daysWithBookings = useMemo(() => {
    const out = new Set<string>();
    for (const b of calendarBookings) {
      const k = bookingDayKey(b);
      if (k) out.add(k);
    }
    return out;
  }, [calendarBookings]);

  const bookingsForSelectedDay = useMemo(
    () =>
      calendarBookings
        .filter((b) =>
          b.preferredDateTime ? isSameDay(new Date(b.preferredDateTime), selectedDate) : false,
        )
        .sort((a, b) => {
          const ta = a.preferredDateTime ?? '';
          const tb = b.preferredDateTime ?? '';
          return ta.localeCompare(tb);
        }),
    [calendarBookings, selectedDate],
  );

  const cancelPending = useMutation({
    mutationFn: (id: string) => bookingsService.customerCancel(id),
    onMutate: (id) => setPendingId(id),
    onSettled: () => setPendingId(null),
    onSuccess: () => {
      notify.success(t('bookings.cancelledSuccess'));
      invalidate();
    },
    onError: (err) => notify.error(translate(err)),
  });

  const requestCancellation = useMutation({
    mutationFn: ({ id, message }: { id: string; message: string }) =>
      bookingsService.customerRequestCancellation(id, message),
    onMutate: (v) => setPendingId(v.id),
    onSettled: () => setPendingId(null),
    onSuccess: () => {
      notify.success(t('bookings.cancellationRequestedSuccess'));
      setRequestTarget(null);
      setRequestMessage('');
      invalidate();
    },
    onError: (err) => notify.error(translate(err)),
  });

  const acceptCounter = useMutation({
    mutationFn: (id: string) => bookingsService.customerAcceptCounter(id),
    onMutate: (id) => setPendingId(id),
    onSettled: () => setPendingId(null),
    onSuccess: () => {
      notify.success(t('bookings.acceptCounterSuccess'));
      invalidate();
    },
    onError: (err) => notify.error(translate(err)),
  });

  const rejectCounter = useMutation({
    mutationFn: (id: string) => bookingsService.customerRejectCounter(id),
    onMutate: (id) => setPendingId(id),
    onSettled: () => setPendingId(null),
    onSuccess: () => {
      notify.success(t('bookings.rejectCounterSuccess'));
      invalidate();
    },
    onError: (err) => notify.error(translate(err)),
  });

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
      <Typography variant="h3" sx={{ mb: 1 }}>
        {t('bookings.customerTitle')}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {t('bookings.customerSubtitle')}
      </Typography>

      <Tabs
        value={view}
        onChange={(_, v: ViewMode) => v && setView(v)}
        sx={{
          mb: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 },
        }}
      >
        <Tab value="list" label={t('bookings.viewList')} />
        <Tab value="calendar" label={t('bookings.viewCalendar')} />
      </Tabs>

      {view === 'list' ? (
        <>
          <BookingFilterTabs value={filter} onChange={setFilter} />

          {isLoading ? (
            <Stack spacing={2}>
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} variant="rounded" height={180} sx={{ borderRadius: 3 }} />
              ))}
            </Stack>
          ) : bookings.length === 0 ? (
            <EmptyState />
          ) : (
            <Stack spacing={2}>
              {bookings.map((b) => (
                <BookingCard
                  key={b.id}
                  booking={b}
                  perspective="customer"
                  actions={customerActions(b)}
                />
              ))}
            </Stack>
          )}
        </>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 3,
                p: { xs: 1, sm: 2 },
              }}
            >
              {calendarQuery.isLoading ? (
                <Skeleton variant="rounded" height={320} />
              ) : (
                <DateCalendar
                  value={selectedDate}
                  onChange={(d) => d && setSelectedDate(startOfDay(d))}
                  minDate={addDays(startOfDay(new Date()), -365)}
                  maxDate={addDays(startOfDay(new Date()), 365)}
                  slots={{
                    day: (props: PickersDayProps<Date>) => (
                      <DayWithDot
                        {...props}
                        hasBooking={daysWithBookings.has(format(props.day, 'yyyy-MM-dd'))}
                      />
                    ),
                  }}
                />
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, textTransform: 'capitalize', mb: 1.5 }}
            >
              {format(selectedDate, 'EEEE d MMMM yyyy', { locale: dateLocale })}
            </Typography>

            {calendarQuery.isLoading ? (
              <Stack spacing={2}>
                {[0, 1].map((i) => (
                  <Skeleton key={i} variant="rounded" height={140} sx={{ borderRadius: 3 }} />
                ))}
              </Stack>
            ) : bookingsForSelectedDay.length === 0 ? (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 4,
                  px: 2,
                  border: '1px dashed',
                  borderColor: 'divider',
                  borderRadius: 3,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {t('bookings.noBookingsForDay')}
                </Typography>
              </Box>
            ) : (
              <Stack spacing={2}>
                {bookingsForSelectedDay.map((b) => (
                  <BookingCard
                    key={b.id}
                    booking={b}
                    perspective="customer"
                    actions={customerActions(b)}
                  />
                ))}
              </Stack>
            )}
          </Grid>
        </Grid>
      )}

      <RequestCancellationDialog
        booking={requestTarget}
        message={requestMessage}
        onMessageChange={setRequestMessage}
        onCancel={() => !pendingId && setRequestTarget(null)}
        onConfirm={() =>
          requestTarget &&
          requestCancellation.mutate({
            id: requestTarget.id,
            message: requestMessage.trim(),
          })
        }
        submitting={Boolean(pendingId)}
      />

      <LeaveReviewDialog
        booking={reviewTarget}
        onClose={() => setReviewTarget(null)}
        onSubmitted={() => {
          setReviewTarget(null);
          invalidate();
        }}
      />

      <BookingChatDrawer bookingId={chatBookingId} onClose={() => setChatBookingId(null)} />
    </Container>
  );

  function customerActions(b: Booking) {
    const isPending = pendingId === b.id;

    // "Message" is offered while the booking is in any active state where
    // the conversation makes sense (not after rejection / cancellation).
    const isActive =
      b.status === 'PENDING'
      || b.status === 'CONFIRMED'
      || b.status === 'CANCELLATION_REQUESTED'
      || b.status === 'COUNTER_PROPOSED';
    const messageAction = isActive
      ? [
          {
            key: 'message',
            label: t('chat.openAction'),
            onClick: () => setChatBookingId(b.id),
            variant: 'outlined' as const,
            color: 'primary' as const,
          },
        ]
      : [];

    // Confirmed bookings always go through the request flow so the provider
    // can apply the refund policy. The red modal explains the outcome before
    // the customer commits.
    if (b.status === 'CONFIRMED') {
      // Past confirmed booking → the prestation happened → offer a review CTA
      // unless one already exists. Same eligibility rule as the backend so the
      // button never appears in an "instant 409" state.
      const dt = b.preferredDateTime ? new Date(b.preferredDateTime).getTime() : null;
      const isPast = dt != null && dt < Date.now();
      const reviewAction =
        isPast && !b.hasReview
          ? [
              {
                key: 'review',
                label: t('reviews.leaveAction'),
                onClick: () => setReviewTarget(b),
                variant: 'contained' as const,
                color: 'primary' as const,
              },
            ]
          : [];
      return [
        ...messageAction,
        ...reviewAction,
        {
          key: 'request-cancellation',
          label: isPending ? t('common.loading') : t('bookings.requestCancellationAction'),
          onClick: () => {
            setRequestTarget(b);
            setRequestMessage('');
          },
          variant: 'outlined' as const,
          color: 'error' as const,
          disabled: isPending,
        },
      ];
    }

    // Pending booking — provider hasn't accepted yet, no payment in play —
    // direct cancel is fine.
    if (b.status === 'PENDING') {
      return [
        ...messageAction,
        {
          key: 'cancel',
          label: isPending ? t('common.loading') : t('bookings.cancelAction'),
          onClick: () => cancelPending.mutate(b.id),
          variant: 'outlined' as const,
          color: 'error' as const,
          disabled: isPending,
        },
      ];
    }

    // Provider counter-proposed a different slot — customer accepts (booking
    // becomes CONFIRMED with the new date) or refuses (booking → REJECTED).
    // The proposed slot is visible on the BookingCard itself; these are the
    // formal actions.
    if (b.status === 'COUNTER_PROPOSED') {
      return [
        ...messageAction,
        {
          key: 'reject-counter',
          label: isPending ? t('common.loading') : t('bookings.rejectCounterAction'),
          onClick: () => rejectCounter.mutate(b.id),
          variant: 'outlined' as const,
          color: 'error' as const,
          disabled: isPending,
        },
        {
          key: 'accept-counter',
          label: isPending ? t('common.loading') : t('bookings.acceptCounterAction'),
          onClick: () => acceptCounter.mutate(b.id),
          variant: 'contained' as const,
          color: 'success' as const,
          disabled: isPending,
        },
      ];
    }

    return messageAction;
  }
}

// ── Red-warning cancellation dialog ──────────────────────────────────────

type RequestCancellationDialogProps = {
  booking: Booking | null;
  message: string;
  onMessageChange: (next: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
  submitting: boolean;
};

function RequestCancellationDialog({
  booking,
  message,
  onMessageChange,
  onCancel,
  onConfirm,
  submitting,
}: RequestCancellationDialogProps) {
  const { t } = useTranslation();
  // const preview = booking ? computeRefundPreview(booking) : null;
  // ↑ disabled for launch — re-enable together with the refund preview JSX below.

  return (
    <Dialog
      open={Boolean(booking)}
      onClose={() => !submitting && onCancel()}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          // Subtle red tint around the whole modal so it reads as a destructive action.
          border: '1px solid',
          borderColor: 'error.main',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          color: 'error.main',
          fontWeight: 800,
        }}
      >
        <WarningAmberRoundedIcon />
        {t('bookings.requestCancellationTitle')}
      </DialogTitle>
      <DialogContent>
        {/* DEFER-TO-PROVIDER NOTICE — launch-time stance: no automatic refund
            schedule shown. The refund-policy Alert and per-booking outcome
            Box below are commented out (preserved for re-enabling once the
            refund flow is finalised). */}
        <Alert severity="info" variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
          <AlertTitle sx={{ fontWeight: 700 }}>{t('bookings.deferPolicyTitle')}</AlertTitle>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {t('bookings.deferPolicyHint')}
          </Typography>
        </Alert>

        {/* CANCELLATION POLICY + REFUND PREVIEW — disabled for launch.
        <Alert severity="error" variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
          <AlertTitle sx={{ fontWeight: 700 }}>{t('bookings.cancellationPolicyTitle')}</AlertTitle>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {t('bookings.cancellationPolicyRefund', {
              days: CANCELLATION_NOTICE_DAYS,
              percent: CANCELLATION_REFUND_PERCENT,
            })}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {t('bookings.cancellationPolicyNoRefund', {
              days: CANCELLATION_NOTICE_DAYS,
            })}
          </Typography>
        </Alert>

        {preview && (
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: (theme) =>
                preview.refundable
                  ? theme.palette.mode === 'dark'
                    ? 'rgba(91,194,116,0.10)'
                    : 'rgba(46,125,50,0.08)'
                  : theme.palette.mode === 'dark'
                    ? 'rgba(244,113,116,0.12)'
                    : 'rgba(198,40,40,0.08)',
              border: '1px solid',
              borderColor: preview.refundable ? 'success.main' : 'error.main',
              mb: 2,
            }}
          >
            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
              {t('bookings.cancellationOutcomeLabel')}
            </Typography>
            {preview.refundable ? (
              <Typography sx={{ mt: 0.5, fontWeight: 600 }}>
                {t('bookings.cancellationOutcomeRefund', {
                  amount: formatPrice(preview.refundCents, preview.currency),
                  total: formatPrice(preview.totalCents, preview.currency),
                  percent: CANCELLATION_REFUND_PERCENT,
                })}
              </Typography>
            ) : (
              <Typography sx={{ mt: 0.5, fontWeight: 700, color: 'error.main' }}>
                {t('bookings.cancellationOutcomeNoRefund')}
              </Typography>
            )}
            {preview.daysUntil != null && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.5 }}
              >
                {t('bookings.cancellationDaysUntil', { count: preview.daysUntil })}
              </Typography>
            )}
          </Box>
        )}
        */}

        <TextField
          label={t('bookings.requestCancellationMessageLabel')}
          value={message}
          onChange={(e) => onMessageChange(e.target.value)}
          multiline
          minRows={3}
          fullWidth
          inputProps={{ maxLength: 500 }}
          helperText={`${message.length}/500`}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel} disabled={submitting}>
          {t('common.back')}
        </Button>
        <Button
          color="error"
          variant="contained"
          disabled={!booking || submitting}
          onClick={onConfirm}
        >
          {submitting ? t('common.loading') : t('bookings.requestCancellationAction')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function DayWithDot(props: PickersDayProps<Date> & { hasBooking: boolean }) {
  const { hasBooking, ...rest } = props;
  return (
    <Box sx={{ position: 'relative' }}>
      <PickersDay {...rest} />
      {hasBooking && !rest.outsideCurrentMonth && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 4,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 5,
            height: 5,
            borderRadius: '50%',
            bgcolor: 'success.main',
            pointerEvents: 'none',
          }}
        />
      )}
    </Box>
  );
}

// Keep TS6133 quiet while the refund-preview UI is commented out — these
// helpers/constants are still used by the disabled JSX above. Drop this
// line when the refund preview is re-enabled.
void [computeRefundPreview, formatPrice, CANCELLATION_NOTICE_DAYS, CANCELLATION_REFUND_PERCENT];

function EmptyState() {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        textAlign: 'center',
        py: { xs: 6, md: 10 },
        px: 2,
        border: '1px dashed',
        borderColor: 'divider',
        borderRadius: 4,
      }}
    >
      <Box
        sx={{
          width: 80,
          height: 80,
          mx: 'auto',
          mb: 2,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: (theme) =>
            `linear-gradient(135deg, ${theme.palette.primary.main}1F 0%, ${theme.palette.secondary.main}1F 100%)`,
          color: 'primary.main',
        }}
      >
        <EventBusyRoundedIcon sx={{ fontSize: 36 }} />
      </Box>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        {t('bookings.customerEmptyTitle')}
      </Typography>
      <Typography color="text.secondary">{t('bookings.customerEmptyHint')}</Typography>
    </Box>
  );
}
